import { useCanManageOrgSettingsState } from '#pplx/frontend/apps/ask/organization/hooks/useCanManageOrgSettings.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { useLocalStorage } from '#pplx/frontend/browser-storage/hooks/useLocalStorage.js';
import { useIntl } from 'react-intl';
/**
 * Hook to access the organization's admin message and track whether the current user has seen it.
 *
 * @returns {AdminMessageData} Object containing:
 *   - canCurrentUserEdit: true for enterprise users with org_settings_write
 *   - message: The admin message with tracking info, or null if:
 *     - No admin message is configured
 *     - Organization doesn't exist
 *     - Organization is not an enterprise org
 */
export function useOrgAdminMessage() {
    const { $t } = useIntl();
    const { organization, isEnterprise } = useUserOrgData({
        reason: 'use-admin-message',
    });
    const value = organization?.settings?.admin_message;
    const orgUUID = organization?.uuid;
    const { canManageOrgSettings } = useCanManageOrgSettingsState({ enabled: !!isEnterprise });
    const canCurrentUserEdit = Boolean(canManageOrgSettings && isEnterprise);
    const [storageValue, setStorageValue] = useLocalStorage('dismissed_admin_messages', {});
    if (!value?.body || !orgUUID || !isEnterprise) {
        return { canCurrentUserEdit, message: null };
    }
    const displayName = organization.display_name;
    const title = displayName
        ? $t({
            description: 'Title for the admin message modal showing the organization name with usage guidelines',
            defaultMessage: "{organizationName}'s usage guidelines",
            id: '8c67a578b5',
        }, {
            organizationName: displayName,
        })
        : $t({
            description: 'Generic title for the admin message modal when organization name is not available',
            defaultMessage: `Your organization's usage guidelines`,
            id: '90429da4d1',
        });
    return {
        canCurrentUserEdit,
        message: {
            title,
            value,
            hasCurrentUserSeen: storageValue[orgUUID] === value.uuid,
            markAsSeen() {
                setStorageValue((prev) => ({ ...prev, [orgUUID]: value.uuid }));
            },
        },
    };
}
//# sourceMappingURL=useOrgAdminMessage.js.map